import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { parseList, serializeList, toggleSort, type SortDirection } from '@/lib/query'

const SEARCH_DEBOUNCE_MS = 350

interface UseListParamsOptions {
  defaultSort: string
  filterFields?: readonly string[]
}

export function useListParams({ defaultSort, filterFields = [] }: UseListParamsOptions) {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParam = searchParams.get('search') ?? ''
  const sort = searchParams.get('sort') || defaultSort
  const fieldsKey = filterFields.join('|')
  const fields = useMemo(() => (fieldsKey === '' ? [] : fieldsKey.split('|')), [fieldsKey])

  const filters = useMemo(
    () => Object.fromEntries(fields.map(field => [field, parseList(searchParams.get(field))])) as Record<string, string[]>,
    [searchParams, fields]
  )

  const patch = useCallback(
    (updates: Record<string, string | null>) =>
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous)
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === '') next.delete(key)
            else next.set(key, value)
          }
          return next
        },
        { replace: true }
      ),
    [setSearchParams]
  )

  const patchRef = useRef(patch)
  patchRef.current = patch

  const [searchInput, setSearchInput] = useState(searchParam)
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam)
  const lastPatched = useRef(searchParam)

  useEffect(() => {
    if (searchParam === lastPatched.current) return
    lastPatched.current = searchParam
    setSearchInput(searchParam)
    setDebouncedSearch(searchParam)
  }, [searchParam])

  useEffect(() => {
    if (searchInput === debouncedSearch) return
    const timer = setTimeout(() => {
      const next = searchInput.trim()
      lastPatched.current = next
      setDebouncedSearch(searchInput)
      patchRef.current({ search: next || null })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput, debouncedSearch])

  const setSearch = useCallback((value: string) => setSearchInput(value), [])

  const setFilter = useCallback((field: string, values: string[]) => patch({ [field]: serializeList(values) || null }), [patch])

  const sortBy = useCallback(
    (field: string, fallback: SortDirection) => {
      const next = toggleSort(sort, field, fallback)
      patch({ sort: next === defaultSort ? null : next })
    },
    [patch, sort, defaultSort]
  )

  const clearAll = useCallback(() => {
    lastPatched.current = ''
    setSearchInput('')
    setDebouncedSearch('')
    patch(Object.fromEntries(['search', ...fields].map(field => [field, null])))
  }, [patch, fields])

  const search = debouncedSearch.trim()
  const activeFilters = Object.values(filters).reduce((total, values) => total + (values.length > 0 ? 1 : 0), 0)

  return {
    searchInput,
    setSearch,
    search: search || undefined,
    isSearchPending: searchInput !== debouncedSearch,
    sort,
    sortBy,
    filters,
    setFilter,
    clearAll,
    hasFilters: search !== '' || activeFilters > 0
  }
}

export type ListParams = ReturnType<typeof useListParams>
